import { type RefObject, useEffect } from 'react';

interface KeyboardInteractionOutsideProps {
  /** Ref element to check the event target against */
  ref: RefObject<Element | null>;
  /** Callback called when a keyboard event is triggered outside the reference element*/
  onInteractOutside?: (e: KeyboardEvent) => void;
  /** Switch to skip all checks */
  isDisabled?: boolean;
}

/** Determines if the event is on a button element and within the given ref */
function isValidEvent(event: KeyboardEvent, ref: RefObject<Element | null>) {
  // if the event target is no longer in the document
  if (event.target) {
    const target = event.target as Node;
    const ownerDocument = target.ownerDocument;
    if (!ownerDocument || !ownerDocument.documentElement.contains(target)) {
      return false;
    }
  }

  return ref.current && !ref.current.contains(event.target as Node);
}

/** Determine a keyboard interaction outside of the given ref */
export function useKeyboardInteractionOutside(
  props: KeyboardInteractionOutsideProps,
) {
  const { ref, onInteractOutside, isDisabled } = props;

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (onInteractOutside && isValidEvent(event, ref)) {
        onInteractOutside(event);
      }
    };

    document.addEventListener('keyup', onKeyUp, true);
    return () => {
      document.removeEventListener('keyup', onKeyUp, true);
    };
  }, [isDisabled, ref, onInteractOutside]);
}
